import type { CatalogEntryResp, PlanResp, PriceResp } from "@/types/billing.ts";
import { PLAN_TIER_LABELS, type BillingInterval, type PlanTier } from "@/types/entitlements.ts";

/**
 * Shared wording for the billing surfaces. Plans are described by their
 * multiple of Pro and their price, never by unit counts: the multiple is the
 * one size users are shown anywhere.
 */

/** "Oct 25, 2026" — a calendar date, for renewals, grants, and scheduled changes. */
export function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * "Sun, Oct 25, 5:00 PM PDT" — the reset moment in the viewer's own zone. The
 * boundary is Monday 00:00 UTC, which is still Sunday across the Americas, so
 * a date alone would name the wrong day there.
 */
export function formatResetMoment(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

/**
 * "$20" or "$16.67" — whole amounts drop their cents. Stripe amounts are in
 * the currency's minor unit, which is not always a hundredth (JPY has none).
 */
export function formatPrice(price: PriceResp) {
  const currency = price.currency.toUpperCase();
  const minorDigits =
    new Intl.NumberFormat(undefined, { style: "currency", currency }).resolvedOptions()
      .maximumFractionDigits ?? 2;
  const amount = price.unitAmount / 10 ** minorDigits;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : minorDigits,
  }).format(amount);
}

/** "$20/mo" or "$200/yr". */
export function formatPricePerInterval(price: PriceResp, interval: BillingInterval) {
  return `${formatPrice(price)}/${interval === "year" ? "yr" : "mo"}`;
}

/** Free's price, written the way a paid plan's would be. */
export const FREE_PRICE_LABEL = "$0";

/** Each plan pitched in a few words, for the Billing rows and the pricing page. */
export const TIER_TAGLINES: Record<PlanTier, string> = {
  free: "Try okthx",
  pro: "For everyday help",
  power: "For people who hand okthx a lot",
  max: "For running your whole day through okthx",
};

/** "monthly" / "annually", for "Billed …". */
export function intervalAdverb(interval: BillingInterval) {
  return interval === "year" ? "annually" : "monthly";
}

/** "monthly" / "annual", for "… billing". */
export function intervalAdjective(interval: BillingInterval) {
  return interval === "year" ? "annual" : "monthly";
}

/** The catalog row for `tier`, or undefined when the plan's catalog lacks it. */
export function catalogEntry(plan: PlanResp, tier: PlanTier) {
  return plan.catalog.find((entry) => entry.tier === tier);
}

/** "5x", or null for a tier sold without a size (Free). */
export function multipleLabel(entry: CatalogEntryResp | undefined) {
  return entry && entry.multiple !== null ? `${entry.multiple}x` : null;
}

/**
 * "Power (5x usage)" or "Max (20x usage, effectively unlimited)" — a tier named
 * with its size, for sentences.
 */
export function planPhrase(plan: PlanResp, tier: PlanTier) {
  const entry = catalogEntry(plan, tier);
  const multiple = multipleLabel(entry);
  if (!multiple) return PLAN_TIER_LABELS[tier];
  const unlimited = entry?.marketedUnlimited ? ", effectively unlimited" : "";
  return `${PLAN_TIER_LABELS[tier]} (${multiple} usage${unlimited})`;
}
